import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { Alert, Box, Button, Divider, IconButton, MenuItem, Modal, Typography, useTheme } from "@mui/material";
import Close from "@mui/icons-material/Close";
import Person from "@mui/icons-material/Person";
import { LoadingButton } from '@mui/lab';
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from 'yup';

import { useSnackBar } from "@frontend/features/common/components/SnackBarContext";
import { useGetAllAgencies } from "@frontend/features/agencies/queries";
import { ControlledSelect } from "@frontend/features/common/components/ControlledSelect";
import { ControlledTextField } from "@frontend/features/common/components/ControlledTextField";
import { countries } from '@frontend/features/common/constants/countries';
import { CountryFlag } from '@frontend/features/common/components/CountryFlag';
import { DocumentType } from '@frontend/features/auth/types';
import { client } from "@frontend/features/common/services";

interface CreateUserClientDTO {
  firstName: string;
  lastName: string;
  agency?: string;
  phoneCountryCode: string;
  phoneNumber: string;
  email?: string;
  address1?: string;
  address2?: string;
  country: string;
  city?: string;
  postalCode?: string;
  documentType?: string;
  documentNumber?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
}

export const CreateUserWithClient = ({ open, onClose }: Props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [result, setResult] = useState<null | { user: string, password: string }>(null);

  const queryClient = useQueryClient();
  const { addToast } = useSnackBar();
  const [loading, setLoading] = useState(false);
  const { data: agencies } = useGetAllAgencies({
    pageSize: 10000,
    currentPage: 1,
    search: '',
  });

  const resolver = yupResolver(
    yup.object().shape({
      firstName: yup.string().required(),
      lastName: yup.string().required(),
      email: yup.string().email().optional(),
      agency: yup.string().optional(),
      phoneCountryCode: yup
        .string()
        .test((value) => value !== 'no-selection')
        .required(),
      phoneNumber: yup.string().required(),
      address1: yup.string().optional(),
      address2: yup.string().optional(),
      country: yup
        .string()
        .test((value) => value !== 'no-selection')
        .required(),
      city: yup.string().optional(),
      postalCode: yup.string().optional(),
      documentType: yup.mixed<DocumentType>().oneOf(Object.values(DocumentType)).optional(),
      documentNumber: yup.string().optional(),
    }),
  );

  const methods = useForm({
    resolver,
  });
  const { handleSubmit } = methods;

  const onSubmit = async (data: CreateUserClientDTO) => {
    setLoading(true);
    try {
      const { data: result } = await client.post('/auth/register-with-client', data);

      result.email && result.password && setResult({
        user: result.email,
        password: result.password
      });
      queryClient.invalidateQueries();
    } catch (e_) {
      addToast({
        message: t('users.create.createdError'),
        color: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFinish = () => {
    setResult(null);
    onClose();
  }

  return (
    <Modal open={open} onClose={onClose} aria-labelledby={t('users.create.title')}>
      <Box
        display="flex"
        flexDirection="column"
        gap={theme.spacing(2)}
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: theme.spacing(3),
          borderRadius: theme.spacing(2),
        }}
      >
        <Box display="flex" gap={theme.spacing(2)}>
          <Box flexGrow={1} display="flex" alignItems="center" gap={theme.spacing(1)}>
            <Person color="disabled" />
            <Typography id="create-user-with-client-modal-title" variant="h6" component="h2">
              {t('users.create.title')}
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <Close color="primary" />
          </IconButton>
        </Box>
        <Divider />
        {(result?.password && result.user) ? <Box>
          <Alert severity="warning">{t('users.create.warning')}</Alert>
          <Box display="flex" flexDirection="column" mt={2} gap={1} mb={2}>
            <Box display="flex" justifyContent="space-between"><Typography variant="subtitle1">{t('users.create.user')}</Typography> <Typography variant="subtitle2">{result.user}</Typography></Box>
            <Box display="flex" justifyContent="space-between"><Typography variant="subtitle1">{t('users.create.password')}</Typography> <Typography variant="subtitle2">{result.password}</Typography></Box>
          </Box>
          <Button variant="contained" color="primary" onClick={handleFinish}>{t('users.create.finish')}</Button>
        </Box> : <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box
              display="grid"
              gridTemplateColumns="repeat(12, 1fr)"
              gap={2}
              sx={{
                maxHeight: 'calc(100vh - 300px)',
                overflowX: 'auto',
                paddingTop: theme.spacing(2),
              }}
            >

              <Box gridColumn="span 12">
                <ControlledSelect
                  variant="outlined"
                  name="agency"
                  control={methods.control}
                  placeholder={t('common.form.select')}
                  label={t('clients.create.agency')}
                  size="small"
                  required
                  errorMessage={t('common.form.required')}
                  fullWidth
                >
                  {agencies?.items.map((agency) => (
                    <MenuItem key={agency.id} value={agency.id}>
                      {agency.name}
                    </MenuItem>
                  ))}
                </ControlledSelect>
              </Box>

              <Box gridColumn="span 12">
                <ControlledTextField
                  control={methods.control}
                  name="firstName"
                  label={t('clients.create.firstName')}
                  placeholder={t('common.form.onlyLetters')}
                  required
                  errorMessage={t('common.form.required')}
                  fullWidth
                />
              </Box>
              <Box gridColumn="span 12">
                <ControlledTextField
                  control={methods.control}
                  name="lastName"
                  label={t('clients.create.lastName')}
                  placeholder={t('common.form.onlyLetters')}
                  required
                  errorMessage={t('common.form.required')}
                  fullWidth
                />
              </Box>
              <Box gridColumn="span 12">
                <ControlledTextField
                  control={methods.control}
                  name="email"
                  label={t('clients.create.email')}
                  placeholder={t('common.form.onlyLetters')}
                  required
                  errorMessage={t('common.form.required')}
                  fullWidth
                />
              </Box>
              <Box gridColumn="span 12">
                <ControlledSelect
                  variant="outlined"
                  name="country"
                  required
                  control={methods.control}
                  placeholder={t('common.form.select')}
                  label={t('clients.create.country')}
                  size="small"
                  errorMessage={t('common.form.required')}
                  fullWidth
                >
                  {countries.map((country) => (
                    <MenuItem key={country.code} value={country.name}>
                      <CountryFlag countryCode={country.code} />
                      {country.name}
                    </MenuItem>
                  ))}
                </ControlledSelect>
              </Box>
              <Box gridColumn="span 12">
                <ControlledTextField
                  control={methods.control}
                  name="city"
                  label={t('clients.create.city')}
                  placeholder={t('common.form.onlyLetters')}
                  required
                  errorMessage={t('common.form.required')}
                  fullWidth
                />
              </Box>
              <Box gridColumn="span 12">
                <ControlledTextField
                  control={methods.control}
                  name="postalCode"
                  label={t('clients.create.postalCode')}
                  placeholder={t('common.form.onlyLetters')}
                  required
                  errorMessage={t('common.form.required')}
                  fullWidth
                />
              </Box>

              <Box gridColumn="span 12">
                <ControlledTextField
                  control={methods.control}
                  name="address1"
                  label={t('clients.create.address1')}
                  placeholder={t('common.form.onlyLetters')}
                  required
                  errorMessage={t('common.form.required')}
                  fullWidth
                />
              </Box>
              <Box gridColumn="span 12">
                <ControlledTextField
                  control={methods.control}
                  name="address2"
                  label={t('clients.create.address2')}
                  placeholder={t('common.form.onlyLetters')}
                  fullWidth
                />
              </Box>
              <Box gridColumn="span 5">
                <ControlledSelect
                  variant="outlined"
                  name="phoneCountryCode"
                  required
                  control={methods.control}
                  placeholder={t('common.form.select')}
                  label={t('clients.create.phoneCountryCode')}
                  size="small"
                  errorMessage={t('common.form.required')}
                  fullWidth
                >
                  {countries.map((country) => (
                    <MenuItem key={country.code} value={country.phoneCode}>
                      <CountryFlag countryCode={country.code} />
                      {country.code} (+{country.phoneCode})
                    </MenuItem>
                  ))}
                </ControlledSelect>
              </Box>
              <Box gridColumn="span 7">
                <ControlledTextField
                  control={methods.control}
                  name="phoneNumber"
                  label={t('clients.create.phoneNumber')}
                  placeholder={t('common.form.onlyNumbers')}
                  required
                  errorMessage={t('common.form.required')}
                  fullWidth
                />
              </Box>
              <Box gridColumn="span 4">
                <ControlledSelect
                  variant="outlined"
                  name="documentType"
                  required
                  control={methods.control}
                  placeholder={t('common.form.select')}
                  label={t('clients.create.documentType')}
                  size="small"
                  errorMessage={t('common.form.required')}
                  fullWidth
                  defaultValue={DocumentType.CI}
                >
                  <MenuItem value={DocumentType.CI}>{DocumentType.CI}</MenuItem>
                  <MenuItem value={DocumentType.DNI}>{DocumentType.DNI}</MenuItem>
                  <MenuItem value={DocumentType.PASSPORT}>{DocumentType.PASSPORT}</MenuItem>
                </ControlledSelect>
              </Box>
              <Box gridColumn="span 8">
                <ControlledTextField
                  control={methods.control}
                  name="documentNumber"
                  label={t('clients.create.documentNumber')}
                  placeholder={t('common.form.onlyNumbers')}
                  required
                  errorMessage={t('common.form.required')}
                  fullWidth
                />
              </Box>
            </Box>
            <Box display="flex" flexDirection="column" gap={theme.spacing(3)} paddingTop={theme.spacing(3)}>
              <Divider />
              <Box
                display="flex"
                justifyContent="space-evenly"
                width="100%"
                gap={theme.spacing(1)}
                sx={{
                  '& > button': {
                    width: 'calc(50% - 4px)',
                  },
                }}
              >
                <Button variant="outlined" color="error" size="medium" onClick={onClose}>
                  {t('common.form.discard')}
                </Button>
                <LoadingButton loading={loading} disabled={loading} variant="contained" color="primary" type="submit" size="medium">
                  {t('common.form.save')}
                </LoadingButton>
              </Box>
            </Box>
          </form>
        </FormProvider>}

      </Box>
    </Modal>
  );
}
